import {
  httpGet,
  httpPost,
  httpPatchMerge,
  httpPatch,
  httpDelete,
} from './http'
import { getCompanyMasterService } from './companyMasterServices'
import type { TEntityId } from '~/types/models'
import type { ICompanyMSService } from '~/types/company/models'
import { MSServiceJustAddLabel } from '~/cynqueModules/dataObjects/MSServiceJustAddLabel'
import { isInstalledCompanyMSService } from '~/cynqueModules/dataObjects/CompanyMSService'
import { doesQualifyToBeHidden } from '~/cynqueModules/dataObjects/ItemAvailabilityStatus'

const apiEntityPath = '/api/companymsservices'

/**
 * One companyMSService, by id.
 *
 * Wanted for the walk UP the Company -> Service Group -> Service chain: the
 * entity carries `masterService` as an IEntitySelectItemWithParent whose parent
 * is the company, so the two fields above a stored msService cost one GET.
 */
export async function getCompanyMSService(id: TEntityId) {
  const callingMn = getCompanyMSService.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<ICompanyMSService>({ callingMn, apiPath })
}

/**
 * The pickable msServices of ONE service group, labelled for a select list.
 *
 * Scoped deliberately. This replaced a `getCompanyMSServicesWithLabels()` that
 * read `/api/companymsservices` whole and handed the Case dialog EVERY
 * msService in the system -- the spec that called it carried a standing note
 * saying it needed a company selector above it to narrow the list.
 *
 * The filter is the one that survived from there, and it is about reality
 * rather than tidiness: a case is raised against a service the customer
 * actually has, so only installed ones (active / suspended / disconnected /
 * reactivated) are offered, and superseded and never-taken rows are not.
 */
export async function getCompanyMSServicesForCompanyMasterServiceId(
  id: TEntityId
) {
  const callingMn = getCompanyMSServicesForCompanyMasterServiceId.name
  const companyMasterService = await getCompanyMasterService(id)
  const msServices = companyMasterService?.companyMSServices ?? []
  const filteredMSServices = msServices
    .filter(
      (mss) => isInstalledCompanyMSService(mss) && !doesQualifyToBeHidden(mss)
    )
    .map((mss) => Object.assign(mss, MSServiceJustAddLabel(mss)))

  console.debug(
    `${callingMn} returning filteredMSServices:`,
    filteredMSServices
  )
  return filteredMSServices
}

export async function getCompanyFromCompanyMSService(id: TEntityId) {
  const callingMn = getCompanyFromCompanyMSService.name
  const apiPath = `${apiEntityPath}/${id}/company`
  return httpGet<ICompanyMSService>({ callingMn, apiPath })
}

export async function createCompanyMSService(entity: ICompanyMSService) {
  const callingMn = createCompanyMSService.name
  const apiPath = apiEntityPath
  return httpPost<ICompanyMSService>({
    callingMn,
    apiPath,
    entity,
    asyncOptions: { doAsync: true },
  })
}

export async function updateCompanyMSService(
  entityData: Partial<ICompanyMSService> | Partial<ICompanyMSService>[],
  id: TEntityId
) {
  const callingMn = updateCompanyMSService.name
  if (Array.isArray(entityData)) {
    const apiPath = `${apiEntityPath}`
    return httpPatch<Partial<ICompanyMSService>[]>({
      callingMn,
      apiPath,
      entityData,
      asyncOptions: { doAsync: true },
    })
  }
  const apiPath = `${apiEntityPath}/${id}`
  return httpPatchMerge<Partial<ICompanyMSService>>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteCompanyMSService(
  entityData: ICompanyMSService | ICompanyMSService[]
) {
  const callingMn = deleteCompanyMSService.name
  const apiPath = apiEntityPath
  return httpDelete<ICompanyMSService | ICompanyMSService[]>({
    callingMn,
    apiPath,
    entityData,
  })
}
